import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Formik } from 'formik';
import { get, isEmpty, set, unset } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import getInScopeEnabledFlags from '~/components/ClaimFlags/getInScopeEnabledFlags';
import useClaimFlagConfigurations from '~/components/ClaimFlags/hooks/useClaimFlagConfigurations';
import useClaimStageConfigurations from '~/components/ClaimStages/hooks/useClaimStageConfigurations';
import { localeDetails } from '~/components/CmsMain/localeGlobals';
import ViewCommunicationCardContainer from '~/components/communications/ViewCommunicationCardContainer';
import ExternalClaimNumber from '~/components/Fnol/NewFnolUI/FnolExternalClaimNumber';
import { FnolTriage } from '~/components/Fnol/NewFnolUI/FnolTriage';
import { useCms } from '~/components/hooks/useCms';
import { useIncidentConfiguration } from '~/components/hooks/useIncidentConfiguration';
import useIsConfigurationFieldSupportedByCategories from '~/components/hooks/useIsConfigurationFieldSupportedByCategories';
import { useLob } from '~/components/hooks/useLob';
import useOrganization from '~/components/OrganizationContext';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { cleanEmptyValues, getBasicFnolValues, getObjectPaths, isFeatureEnabled, reportAxiosError } from '~/Utils';

import { FsIndexPanel } from '../../../core';
import {
  cleanConfiguredFieldsFormikValuesBeforeSend,
  getConfiguredFieldsEmptyFormikInitialValues,
  getConfiguredFieldsValidations,
} from '../../../IncidentConfiguration/ConfiguredFields';
import {
  activeFieldGroups,
  emptyFieldGroupFormikValues,
  fieldGroupKeys,
  fieldGroupValidations,
} from '../../../IncidentConfiguration/HierarchyFieldGroup/hierarchyTree';
import { usePolicy } from '../../../PolicyContainer';
import AdditionalInformation, {
  getIsAdditionalInformationActive,
  getSectionsValidationSchema,
} from '../AdditionalInformation';
import {
  getCustomAdditionalInformationSections,
  getCustomSectionPath,
  getFieldGroupsForSection,
} from '../AdditionalInformation/Fragments/CustomSectionFragment';
import {
  getSupplementaryInformationFieldGroups,
  mapAutoSupplementaryInformationToIncidentAdditional,
  mapSupplementaryInformationToIncident,
  SUPPLEMENTARY_INFORMATION_PATH,
} from '../AdditionalInformation/Fragments/SupplementaryInformationFragment';
import FnolFeedback from '../FnolFeedback';
import FnolFiles from '../FnolFiles';
import FnolFooter, { getFooterInitialValues, getFooterValidation } from '../FnolFooter';
import FnolHeader from '../FnolHeader';
import FnolHeadline, { getVerifiedInsuredInfoValidation } from '../FnolHeadline';
import FnolWitnesses from '../FnolWitnesses';
import {
  getWitnessesIncidentInitialValues,
  getWitnessesIncidentValidationSchema,
} from '../FnolWitnesses/WitnessSummary';
import {
  getIncidentDetailsValidationSchema,
  getPredefinedIncidentDetailsFieldsValidations,
  IncidentDetailsCardFormik,
  incidentDetailsInitialValues,
  mapValuesToIncidentDetails,
} from '../IncidentDetails';
import {
  buildLobSpecificParties,
  getLobSpecificPartiesInitialValues,
  getLobSpecificPartiesValidationSchema,
  InvolvedPartiesCardFormik,
} from '../InvolvedParties';
import SectionWrapper from '../SectionWrapper';

import { applyCreateLikeDraft } from './applyCreateLikeDraft';

const createContactIdsToRefIdMapping = (valuesToSend, contactIdsPaths) => {
  const idToRefId = {};
  contactIdsPaths.forEach((path) => {
    const contactId = get(valuesToSend, path);
    if (!contactId) {
      return;
    }
    const contactPath = path.slice(0, -1 * '_id'.length);
    const contact = get(valuesToSend, contactPath);
    if (!contact) {
      return;
    }
    if (!contact.ref_id) {
      contact.ref_id = contactId.toString();
    }
    idToRefId[contactId] = contact.ref_id;
  });

  return idToRefId;
};

const prepareContactToSend = (contact) => {
  const emails = contact?.emails?.map((email) => ({ email_address: email.email, ...email })) || [];
  return {
    ...contact,
    emails,
    address1: contact.street_address1,
    address2: contact.street_address2,
    creation_mode: 'existing',
  };
};

const populateContactsInValuesToSend = (valuesToSend, policy, hierarchyGroupKeys = []) => {
  const groupPaths = hierarchyGroupKeys.map((key) => `incident_details.additional_fields.${key}`);
  const contactIdsPaths = getObjectPaths(valuesToSend).filter(
    (path) =>
      path.endsWith('contact_id') &&
      !groupPaths.some((groupPath) => path === groupPath || path.startsWith(`${groupPath}.`))
  );
  const idToRefId = createContactIdsToRefIdMapping(valuesToSend, contactIdsPaths);

  contactIdsPaths.forEach((path) => {
    const contactId = get(valuesToSend, path);
    if (!contactId) {
      unset(valuesToSend, path);
      return;
    }
    const contactPath = path.slice(0, -1 * '_id'.length);
    const contact = get(valuesToSend, contactPath);
    const contactRefId = idToRefId[contactId];

    const pathWithRefId = path.replace('contact_id', 'contact_ref_id');
    unset(valuesToSend, path);
    set(valuesToSend, pathWithRefId, contactRefId);

    if (!isEmpty(contact) && !valuesToSend.contacts.find((contact) => contact.ref_id === contactRefId)) {
      valuesToSend.contacts.push(prepareContactToSend(contact));
    }
  });

  policy.contacts.forEach((policyContact) => {
    if (valuesToSend.contacts.find((contact) => contact.id === policyContact.id)) {
      return;
    }
    policyContact['ref_id'] = policyContact.id.toString();
    valuesToSend.contacts.push(prepareContactToSend(policyContact));
  });

  return valuesToSend;
};

const FnolScreen = ({
  partiesStore,
  onChangePartiesStore,
  onSaveDraft,
  onSubTypeChange,
  communicationId,
  fnolAiRecommendationId,
  showCliveWarning,
  mixpanelExtra,
  hasFnolAiRecommendations,
  inScopeEnabledFlags,
}) => {
  const { userOrganization } = useCms();
  const { incidentConfiguration } = useIncidentConfiguration();
  const { lob } = useLob();
  const { isFieldSupportedByCategories } = useIsConfigurationFieldSupportedByCategories();
  const isHierarchyFieldGroupsEnabled = isFeatureEnabled(
    userOrganization,
    CONFIGURATION_FEATURES_NAMES.HIERARCHY_FIELD_GROUPS
  );
  const [failedAttachments, setFailedAttachments] = useState([]);

  const handleCommunicationLoaded = useCallback((communication) => {
    const failed =
      communication?.attachments?.filter((attachment) =>
        attachment?.clive_store_information?.processing_status?.startsWith('error')
      ) || [];
    setFailedAttachments(failed);
  }, []);

  return (
    <div className="h-auto">
      <PanelGroup direction="horizontal" id="group-fnol-form" className="absolute inset-0 flex w-full gap-4">
        <Panel id="fnol-form-display" defaultSize={65}>
          <FnolHeader />
          <div className="h-screen overflow-scroll">
            <div className="mt-40">
              <FnolHeadline
                showCliveWarning={showCliveWarning}
                hasFnolAiRecommendations={hasFnolAiRecommendations}
                failedAttachments={failedAttachments}
              />
              <FsIndexPanel
                sections={[
                  {
                    id: 'incident_details',
                    title: 'Incident Details',
                    node: <IncidentDetailsCardFormik onSubTypeChange={onSubTypeChange} />,
                  },
                  {
                    id: 'involved_parties',
                    title: 'Involved Parties',
                    node: (
                      <InvolvedPartiesCardFormik
                        partiesStore={partiesStore}
                        onChangePartiesStore={onChangePartiesStore}
                      />
                    ),
                  },
                  {
                    id: 'additional_information',
                    title: 'Additional Information',
                    node: <AdditionalInformation />,
                    isHidden: !getIsAdditionalInformationActive(
                      incidentConfiguration,
                      lob,
                      isHierarchyFieldGroupsEnabled,
                      isFieldSupportedByCategories
                    ),
                  },
                  {
                    id: 'witnesses',
                    title: 'Witnesses',
                    node: <FnolWitnesses />,
                    isHidden: !incidentConfiguration?.witnesses?.active,
                  },
                  {
                    id: 'triage',
                    title: 'Triage',
                    node: <FnolTriage />,
                    isHidden: !incidentConfiguration?.triage?.active,
                  },
                  {
                    id: 'external_claim_numbers',
                    title: 'External Claim Numbers',
                    node: <ExternalClaimNumber />,
                    isHidden: !incidentConfiguration?.external_claim_numbers?.active,
                  },
                  {
                    id: 'files',
                    title: 'Files',
                    node: <FnolFiles />,
                  },
                  {
                    id: 'feedback',
                    title: 'How did Clive do?',
                    node: <FnolFeedback />,
                    isHidden: !hasFnolAiRecommendations,
                  },
                ]}
                panelTitle="FNOL Content"
                SectionWrapperComponent={SectionWrapper}
              />
              {/* Gap between end of sections and the footer */}
              {Array.from(Array(16).keys()).map((item, index) => (
                <SectionWrapper key={index} />
              ))}
            </div>
          </div>
          <FnolFooter
            onSaveDraft={onSaveDraft}
            partiesStore={partiesStore}
            hideSaveDraft={!!fnolAiRecommendationId}
            mixpanelExtra={mixpanelExtra}
            inScopeEnabledFlags={inScopeEnabledFlags}
          />
        </Panel>
        {communicationId && (
          <>
            <PanelResizeHandle id="resize-handle" className="mx-12 w-2 rounded-lg bg-slate-500" />
            <Panel
              id="communication-display"
              className="mt-40 bg-white pt-32"
              defaultSize={35}
              minSize={25}
              maxSize={80}
            >
              <div className="h-full overflow-scroll">
                <div className="mb-80">
                  <ViewCommunicationCardContainer
                    communicationId={communicationId}
                    disableAdditionalActions
                    displayAttachClaim={false}
                    isDialog={false}
                    onCommunicationLoaded={handleCommunicationLoaded}
                  />
                </div>
              </div>
            </Panel>
          </>
        )}
      </PanelGroup>
    </div>
  );
};

FnolScreen.propTypes = {
  policy: PropTypes.object.isRequired,
  incidentConfiguration: PropTypes.object.isRequired,
  partiesStore: PropTypes.object.isRequired,
  onChangePartiesStore: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  onSaveDraft: PropTypes.func.isRequired,
  onSubTypeChange: PropTypes.func,
  communicationId: PropTypes.number,
  fnolRecommendationId: PropTypes.string,
  fnolAiRecommendationId: PropTypes.string,
  showCliveWarning: PropTypes.bool,
  mixpanelExtra: PropTypes.object,
  hasFnolAiRecommendations: PropTypes.bool,
  inScopeEnabledFlags: PropTypes.array,
};

// Fold every custom section's configured-field values into one flat map for the additional_fields payload.
// This branches on the presence of the additional_information_sections key; a config without it
// contributes nothing.
const getCustomSectionsConfiguredFieldValues = (incidentConfiguration, values) =>
  getCustomAdditionalInformationSections(incidentConfiguration).reduce((acc, section) => {
    const sectionValues = get(values, getCustomSectionPath(section.id), {})?.configured_fields_values;
    return sectionValues ? Object.assign(acc, sectionValues) : acc;
  }, {});

// Every section's values fold into one additional_fields map at submit, so the hierarchy group allowlist takes
// each section's groups, through the helper that section renders them with.
const getSubmittedFieldGroupKeys = (incidentConfiguration, isHierarchyFieldGroupsEnabled) =>
  isHierarchyFieldGroupsEnabled
    ? fieldGroupKeys([
        ...activeFieldGroups(incidentConfiguration, () => true, 'incident_details'),
        ...getSupplementaryInformationFieldGroups(incidentConfiguration, undefined, true),
        ...getCustomAdditionalInformationSections(incidentConfiguration).flatMap((section) =>
          getFieldGroupsForSection(incidentConfiguration, section.id, undefined, true)
        ),
      ])
    : [];

// Seed a verified_<id> flag (false) for each custom section, matching the auto sections' verified_ seeds.
const getCustomSectionsVerifiedInitialValues = (incidentConfiguration) =>
  Object.fromEntries(
    getCustomAdditionalInformationSections(incidentConfiguration).map((section) => [`verified_${section.id}`, false])
  );

// The manual FNOL endpoint's create_like_context, from the route state CreateLikeClaimDialog pushes.
// A carry-over the route state does not mention is sent as not requested.
const buildCreateLikeContextPayload = (createLikeContext) => ({
  source_claim_id: createLikeContext.sourceClaimId,
  copy_documents: !!createLikeContext.copyDocuments,
  copy_notes: !!createLikeContext.copyNotes,
  copy_communications: !!createLikeContext.copyCommunications,
  copy_contacts: !!createLikeContext.copyContacts,
  copy_legal_actions: !!createLikeContext.copyLegalActions,
});

const FnolScreenFormik = ({
  onSubmitDraft,
  fnolDraft,
  subOrganizationId,
  claimValues,
  onSubmitFnol,
  communicationId,
  fnolAiRecommendationId = null,
  showCliveWarning,
  mixpanelExtra = null,
  hasFnolAiRecommendations = false,
  createLikeContext = null,
}) => {
  const { userOrganization } = useCms();
  const { policy } = usePolicy();
  const { claimType, lob } = useLob();
  const { incidentConfiguration } = useIncidentConfiguration();
  const [partiesStore, setPartiesStore] = useState(fnolDraft ? fnolDraft.draft.partiesStore || {} : {});
  const { isFieldSupportedByCategories } = useIsConfigurationFieldSupportedByCategories();
  const { claimStageConfigurations, isOperational: isClaimStageOperational } = useClaimStageConfigurations();
  const enabledStageKeys = useMemo(
    () => claimStageConfigurations.filter((c) => c.is_enabled).map((c) => c.key),
    [claimStageConfigurations]
  );
  const initialStageKey = useMemo(() => {
    if (!isClaimStageOperational) return null;
    const initialStage = claimStageConfigurations.find((c) => c.is_initial && c.is_enabled);
    return initialStage?.key || null;
  }, [claimStageConfigurations, isClaimStageOperational]);
  const { claimFlagConfigurations } = useClaimFlagConfigurations();
  const { subOrganizationEnabled } = useOrganization();
  // The claim's sub-org is the selected policy's, matching the backend scope check
  // (claim.policy.sub_organization_id). The `subOrganizationId` prop is only set on the manual
  // loss-without-policy path, so it can't be the scope source for the normal policy-search flow.
  const claimSubOrganizationId = policy?.sub_organization?.id ?? null;
  const isHierarchyFieldGroupsEnabled = isFeatureEnabled(
    userOrganization,
    CONFIGURATION_FEATURES_NAMES.HIERARCHY_FIELD_GROUPS
  );
  // Incident Details group roots need their own form initialization and validation; the other sections'
  // dialogs seed and validate their groups themselves.
  const hierarchyFieldGroupsFor = (categoryFilter) =>
    isHierarchyFieldGroupsEnabled ? activeFieldGroups(incidentConfiguration, categoryFilter, 'incident_details') : [];
  const hierarchyFieldGroupKeys = getSubmittedFieldGroupKeys(incidentConfiguration, isHierarchyFieldGroupsEnabled);
  const inScopeEnabledFlags = useMemo(
    () =>
      getInScopeEnabledFlags(claimFlagConfigurations, {
        lob,
        subOrganizationId: claimSubOrganizationId,
        subOrganizationEnabled,
      }),
    [claimFlagConfigurations, lob, claimSubOrganizationId, subOrganizationEnabled]
  );
  const enabledInScopeFlagKeys = useMemo(() => inScopeEnabledFlags.map((flag) => flag.key), [inScopeEnabledFlags]);
  // Seed from the draft so a resumed draft's sub-type is known before the user touches the type picker —
  // otherwise the sub-type-bound configured-field and section validations would treat every field as
  // out-of-sub-type on resume (mirrors the draft-seeding of partiesStore above).
  const [subType, setSubType] = useState(fnolDraft?.draft?.incident_sub_type);
  const [type, setType] = useState(fnolDraft?.draft?.incident_type);
  const external_id = useRef(uuidv4());

  const handleSubmitDraft = async (values, setSubmitting) => {
    await onSubmitDraft({ ...values, partiesStore });
    setSubmitting(false);
  };

  const extractExternalClaimNumberForSave = (externalClaimNumber) => {
    return {
      external_claim_number: externalClaimNumber.external_claim_number,
      external_contact: externalClaimNumber.external_contact || null,
      external_contact_id: externalClaimNumber.external_contact?.id,
      description: externalClaimNumber.description,
    };
  };

  const handleSubmitFnol = async (values) => {
    // Only submit flags the field is actually offering: intersect the selection with the enabled
    // in-scope keys and require the field to be active. This drops stale/out-of-scope selections
    // (e.g. left over from a draft or a since-narrowed scope) that the hidden field would otherwise send.
    const claimFlagsActive = !!incidentConfiguration.fnol?.claim_flags?.active;
    const submittedFlagKeys = claimFlagsActive
      ? (values?.claim_flag_keys ?? []).filter((key) => enabledInScopeFlagKeys.includes(key))
      : [];
    const valuesToSend = {
      external_id: external_id.current,
      sub_organization_external_id: policy?.sub_organization?.external_id,
      lob,
      fnol_policyholder_contact_id: policy.insured_contact_id,
      submission_mode: values?.submission_mode,
      reported_date: values?.reported_date,
      stage_key: values?.stage_key ?? undefined,
      claim_flag_keys: submittedFlagKeys.length ? submittedFlagKeys : undefined,
      policy_retrieval_object: {
        retrieval_mode: policy?.is_manual ? 'manual' : 'policy_api_uid',
        policy_holder_contact_id: policy?.insured_contact_id,
        policy_holder_contact: policy?.insured_contact,
        policy_api_uid: policy?.api_uid,
        policy: {
          is_claims_made: policy?.is_claims_made,
        },
      },
      incident_details: {
        ...cleanEmptyValues(mapValuesToIncidentDetails(values)),
        ...cleanEmptyValues(mapSupplementaryInformationToIncident(values)),
        additional_fields: cleanConfiguredFieldsFormikValuesBeforeSend(
          incidentConfiguration.incident_details.configured_fields,
          {
            ...values.configured_fields_values,
            ...get(values, SUPPLEMENTARY_INFORMATION_PATH, {})?.configured_fields_values,
            ...getCustomSectionsConfiguredFieldValues(incidentConfiguration, values),
          },
          { fieldGroupKeys: hierarchyFieldGroupKeys }
        ),
        auto_incident_additional: {
          // The LOB-specific half of the Supplementary Information section lives here, one level
          // down from the base fields above — the shape AutoSpecificFnol.fill_lob_incident reads.
          ...cleanEmptyValues(mapAutoSupplementaryInformationToIncidentAdditional(values, lob)),
          fault_assessment: {
            ...cleanEmptyValues(values?.incident_details?.auto_incident_additional?.fault_assessment),
          },
          theft_follow_up: {
            ...cleanEmptyValues(values?.incident_details?.auto_incident_additional?.theft_follow_up),
          },
          accident_follow_up: {
            ...cleanEmptyValues(values?.incident_details?.auto_incident_additional?.accident_follow_up),
          },
          police_and_fire_department: {
            ...cleanEmptyValues(values?.incident_details?.auto_incident_additional?.police_and_fire_department),
          },
        },
        witnesses: values?.witnesses || [],
      },
      involved_parties: buildLobSpecificParties(claimType)({ values }),
      contacts: [],
      // Only while the Files card shows them: with the section inactive nothing is attached, but the submit
      // still validates a document a draft left in the form, which nobody could then correct.
      documents: incidentConfiguration.documents?.active ? values?.documents || [] : [],
      triage: values?.triage || {},
      external_claim_numbers: values?.external_claim_numbers?.map(extractExternalClaimNumberForSave) || [],
      // Feedback is only meaningful when Clive drafted the FNOL — include it only on the
      // inbox-submission path. The manual path's endpoint doesn't read it.
      ...(fnolAiRecommendationId ? { clive_feedback: values?.clive_feedback?.trim() || null } : {}),
      // Mirror image of the above: only the manual FNOL endpoint reads create_like_context. The
      // inbox endpoint would pass it straight through to FnolManager, inflating field_density_score
      // and writing no link or notes.
      ...(createLikeContext && !fnolAiRecommendationId
        ? { create_like_context: buildCreateLikeContextPayload(createLikeContext) }
        : {}),
    };

    // for when the AI decides on claim number
    if (values.claim_number) {
      valuesToSend.claim_number = values.claim_number;
    }

    // If the FNOL created from Fnol Ai Inbox, save it with the correct api
    const url = fnolAiRecommendationId
      ? `/api/v1/fnol_inbox/inbox/${fnolAiRecommendationId}/fnol`
      : `/api/v1/claims/fnol/${userOrganization.id}`;

    try {
      const res = await axios.post(url, populateContactsInValuesToSend(valuesToSend, policy, hierarchyFieldGroupKeys));
      if (res.data.create_like_carry_over_failed) {
        // No list of material, as in the backend's failure note: the dialog lets the user pick any
        // subset, and the whole copy is rolled back together. External claim numbers are not part of
        // the copy: they travel in this FNOL payload, so a failed copy leaves them on the claim.
        toast.error(
          'The claim was created, but copying the selected material from the original claim failed. Copy anything this claim needs manually.'
        );
      }
      onSubmitFnol(res.data.id);
    } catch (error) {
      reportAxiosError(error);
    }
  };

  const getSeededFnolInitialValues = () => ({
    ...getBasicFnolValues(),
    policy,
    policy_id: policy.id,
    is_manual_policy: !!policy.is_manual,
    sub_organization_id: subOrganizationId ?? '',
    primary_contact_id: '',
    witnesses: [],
    triage: {},
    external_claim_numbers: [],
    clive_feedback: '',
    verified_supplementary_information: false,
    verified_initial_fault_assessment: false,
    verified_police_and_fire_department: false,
    verified_initial_theft_follow_up: false,
    verified_initial_accident_follow_up: false,
    // Config-driven custom sections, keyed on the additional_information_sections list; a config without
    // that key seeds nothing.
    ...getCustomSectionsVerifiedInitialValues(incidentConfiguration),
    verified_insured_info: !!policy.is_manual,
    fnol_policyholder_contact_id: policy.insured_contact_id,
    ...incidentDetailsInitialValues(claimValues, policy),
    loss_location: {
      country: policy?.insured_property_location?.country || localeDetails.locale.region,
      description: policy?.insured_property_location?.description || '',
      address1: policy?.insured_property_location?.address1 || '',
      address2: policy?.insured_property_location?.address2 || '',
      city: policy?.insured_property_location?.city || '',
      county: policy?.insured_property_location?.county || '',
      state: policy?.insured_property_location?.state || '',
      zipcode: policy?.insured_property_location?.zipcode || '',
      country_in_uk: policy?.insured_property_location?.country_in_uk || '',
      is_highway: policy?.insured_property_location?.is_highway || false,
    },
    ...getWitnessesIncidentInitialValues(incidentConfiguration),
    ...getLobSpecificPartiesInitialValues(claimType),
    configured_fields_values: {
      ...getConfiguredFieldsEmptyFormikInitialValues(
        incidentConfiguration.incident_details.configured_fields,
        'incident_details',
        (field) => isFieldSupportedByCategories(field)
      ),
      ...emptyFieldGroupFormikValues(hierarchyFieldGroupsFor((group) => isFieldSupportedByCategories(group))),
    },
    ...getFooterInitialValues(incidentConfiguration, claimValues, initialStageKey),
  });

  const mapPropsToFormikInitialValues = () =>
    applyCreateLikeDraft(getSeededFnolInitialValues(), fnolDraft?.draft, incidentConfiguration);

  const handleSubTypeChange = (value, formikContext) => {
    setSubType(value);
    setType(formikContext.values.incident_type);
    formikContext.setFieldValue('configured_fields_values', {
      ...getConfiguredFieldsEmptyFormikInitialValues(
        incidentConfiguration.incident_details.configured_fields,
        'incident_details',
        (field) => isFieldSupportedByCategories(field, formikContext.values.incident_type, value)
      ),
      ...emptyFieldGroupFormikValues(
        hierarchyFieldGroupsFor((group) =>
          isFieldSupportedByCategories(group, formikContext.values.incident_type, value)
        )
      ),
      ...formikContext.values.configured_fields_values,
    });
  };

  return (
    <>
      <Formik
        initialValues={mapPropsToFormikInitialValues()}
        validationSchema={Yup.object().shape({
          ...getPredefinedIncidentDetailsFieldsValidations(incidentConfiguration),
          ...getIncidentDetailsValidationSchema(incidentConfiguration),
          configured_fields_values: Yup.object().shape({
            ...getConfiguredFieldsValidations(incidentConfiguration, 'incident_details', (field) =>
              isFieldSupportedByCategories(field, type, subType)
            ),
            // Apply the same category filter as the rendered groups.
            ...fieldGroupValidations(
              hierarchyFieldGroupsFor((group) => isFieldSupportedByCategories(group, type, subType))
            ),
          }),
          ...getLobSpecificPartiesValidationSchema(claimType, partiesStore),
          ...getFooterValidation(incidentConfiguration, enabledStageKeys, enabledInScopeFlagKeys),
          ...getWitnessesIncidentValidationSchema(incidentConfiguration),
          verified_insured_info: getVerifiedInsuredInfoValidation(incidentConfiguration),
          ...getSectionsValidationSchema(
            lob,
            incidentConfiguration,
            (field) => isFieldSupportedByCategories(field, type, subType),
            isHierarchyFieldGroupsEnabled
          ),
        })}
        onSubmit={async (values, formikProps) => {
          const { setSubmitting } = formikProps;
          setSubmitting(true);
          await handleSubmitFnol(values);
          setSubmitting(false);
        }}
      >
        {(formikProps) => (
          <FnolScreen
            {...formikProps}
            policy={policy}
            partiesStore={partiesStore}
            onChangePartiesStore={setPartiesStore}
            onSubTypeChange={handleSubTypeChange}
            onSaveDraft={handleSubmitDraft}
            fnolDraft={fnolDraft}
            subOrganizationId={subOrganizationId}
            incidentConfiguration={incidentConfiguration}
            communicationId={communicationId}
            fnolAiRecommendationId={fnolAiRecommendationId}
            showCliveWarning={showCliveWarning}
            mixpanelExtra={mixpanelExtra}
            hasFnolAiRecommendations={hasFnolAiRecommendations}
            inScopeEnabledFlags={inScopeEnabledFlags}
          />
        )}
      </Formik>
    </>
  );
};

FnolScreenFormik.propTypes = {
  onSubmitFnol: PropTypes.func.isRequired,
  claimValues: PropTypes.object,
  onSubmitDraft: PropTypes.func.isRequired,
  fnolDraft: PropTypes.object,
  subOrganizationId: PropTypes.number,
  onSubTypeChange: PropTypes.func,
  communicationId: PropTypes.number,
  fnolAiRecommendationId: PropTypes.string,
  showCliveWarning: PropTypes.bool,
  mixpanelExtra: PropTypes.object,
  hasFnolAiRecommendations: PropTypes.bool,
  createLikeContext: PropTypes.shape({ sourceClaimId: PropTypes.number }),
};

export {
  buildCreateLikeContextPayload,
  getCustomSectionsConfiguredFieldValues,
  getCustomSectionsVerifiedInitialValues,
  getSubmittedFieldGroupKeys,
  populateContactsInValuesToSend,
};
export default FnolScreenFormik;
